import React, { useEffect, useMemo, useState } from "react";
import { Seo } from "@/components/Seo";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { VALUES, SKILLS, skillLabel } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { LocationSearch } from "@/components/LocationSearch";
import { BackLink } from "@/components/BackLink";
import MapLeaflet from "@/components/MapLeaflet";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Req, ERR_CLS } from "@/components/RequiredMark";
import { CalendarHeart, Navigation, ShieldCheck } from "lucide-react";

// Phase 15: static option lists live at module scope so they aren't
// re-created as new array/object identities on every render.
const MODE_OPTIONS = [{ v: false, l: "In person" }, { v: true, l: "Remote / online" }];

const toLocalInput = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function PostOpportunity() {
  const nav = useNavigate();
  const { editId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [days, setDays] = useState([]);
  const [loaded, setLoaded] = useState(!editId);
  useEffect(() => { api.awarenessDays(4).then(setDays).catch(() => {}); }, []);

  // Fetch collectives the current user belongs to — controls the "Host under a
  // collective" dropdown. Phase 2: any MEMBER can host (was leader-only).
  useEffect(() => {
    if (!user) return;
    api.myCollectives()
      .then((d) => {
        const cols = d.collectives || [];
        setLedCollectives(cols);
        // Deep link from a collective page (?collective=col_xyz) preselects
        // the "Host under a collective" dropdown — members only (the id must
        // be in the user's own collectives list).
        const pre = searchParams.get("collective");
        if (pre && !editId && cols.some((c) => c.id === pre)) setCollectiveId(pre);
      })
      .catch(() => setLedCollectives([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams/editId read once at auth-ready; re-running on URL churn would clobber the user's picker choice
  }, [user]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [hours, setHours] = useState(2);
  const [eventDate, setEventDate] = useState("");
  // 18-A2 item 3: opt-in campaign tag — checkbox appears when a campaign is
  // active and the chosen date falls inside its range; ?campaign= pre-ticks.
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignOptIn, setCampaignOptIn] = useState(false);
  useEffect(() => {
    api.activeCampaign().then((r) => {
      if (r.campaign) {
        setActiveCampaign(r.campaign);
        if (searchParams.get("campaign") === r.campaign.slug) setCampaignOptIn(true);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const eventDay = eventDate ? eventDate.slice(0, 10) : "";
  const campaignEligible = Boolean(
    activeCampaign && eventDay &&
    eventDay >= activeCampaign.starts_on && eventDay <= activeCampaign.ends_on);
  const [closesAt, setClosesAt] = useState("");
  const [values, setValues] = useState(new Set());
  const [skills, setSkills] = useState(new Set());
  const [coverImage, setCoverImage] = useState("");
  const [remote, setRemote] = useState(false);
  const [recurrence, setRecurrence] = useState("none");
  const [weekDays, setWeekDays] = useState(new Set());
  const [maxVolunteers, setMaxVolunteers] = useState("");
  const [minVolunteers, setMinVolunteers] = useState(""); // A2: minimum attendees
  const [commitmentType, setCommitmentType] = useState("");
  const [collectiveId, setCollectiveId] = useState("");
  const [ledCollectives, setLedCollectives] = useState([]);
  // B4: tier 1-2 volunteer requirements — self-attested by applicants
  const [requiredAge, setRequiredAge] = useState(""); // "" | "16" | "18" | "21"
  const [dbsRequired, setDbsRequired] = useState(false);
  const [reqSkills, setReqSkills] = useState(new Set());
  const [errors, setErrors] = useState({});
  const clearErr = (k) => setErrors((prev) => (prev[k] ? { ...prev, [k]: false } : prev));

  useEffect(() => {
    if (!editId || !user) return;
    api.getOpportunity(editId).then((o) => {
      if (o.org_user_id !== user.user_id) {
        toast.error("You can only edit your own events");
        return nav(`/opportunities/${editId}`);
      }
      setTitle(o.title); setDescription(o.description);
      setLocation(o.location || ""); setLat(o.lat ?? null); setLng(o.lng ?? null);
      setHours(o.hours_required); setEventDate(toLocalInput(o.event_date));
      setClosesAt(o.closes_at ? o.closes_at.slice(0, 10) : "");
      setValues(new Set(o.values || [])); setSkills(new Set(o.skills_needed || []));
      setCoverImage(o.cover_image || ""); setRemote(!!o.remote);
      setRecurrence(o.recurrence || "none"); setWeekDays(new Set(o.recurrence_weekly_days || []));
      setMaxVolunteers(typeof o.max_volunteers === "number" ? String(o.max_volunteers) : "");
      setMinVolunteers(typeof o.min_volunteers === "number" ? String(o.min_volunteers) : "");
      setCommitmentType(o.commitment_type || "");
      setCollectiveId(o.collective_id || "");
      setRequiredAge(o.required_age ? String(o.required_age) : "");
      setDbsRequired(!!o.dbs_required);
      setReqSkills(new Set(o.required_skills || []));
      setLoaded(true);
    }).catch(() => nav("/dashboard"));
  }, [editId, user]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const toggleDay = (d) => {
    const n = new Set(weekDays);
    n.has(d) ? n.delete(d) : n.add(d);
    setWeekDays(n);
  };

  const onCoverChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const res = await api.uploadImage(f);
      setCoverImage(res.url);
      toast.success("Cover uploaded");
    } catch { toast.error("Upload failed"); }
  };

  // Phase 15: stable identities for the live map-preview props — this form
  // re-renders on every keystroke, so inline literals would churn the map.
  const previewMarkers = useMemo(
    () => [{ lat, lng, title: title || "Your event", subtitle: location }],
    [lat, lng, title, location]
  );
  const previewCenter = useMemo(() => [lat, lng], [lat, lng]);

  if (!user) {
    return <div className="max-w-2xl mx-auto px-6 py-16">
      <h1 className="font-display text-2xl text-forest">Sign in to post</h1>
      <p className="text-neutral-600 mt-2">Organisations post opportunities; anyone can host a community event.</p>
    </div>;
  }
  const isOrg = user.user_type === "organisation";

  const toggle = (set, setter, v) => {
    const next = new Set(set);
    next.has(v) ? next.delete(v) : next.add(v);
    setter(next);
  };

  const submit = async () => {
    const missing = {
      title: !title.trim(),
      description: !description.trim(),
      eventDate: !eventDate,
      location: !remote && !location.trim(),
    };
    setErrors(missing);
    if (Object.values(missing).some(Boolean)) return toast.error("Fill the required fields — highlighted in red");
    let maxV = null;
    if (maxVolunteers !== "") {
      const n = parseInt(maxVolunteers, 10);
      if (!Number.isInteger(n) || n < 1) return toast.error("Max volunteers must be a whole number ≥ 1 (or leave blank)");
      maxV = n;
    }
    let minV = null;
    if (minVolunteers !== "") {
      const n = parseInt(minVolunteers, 10);
      if (!Number.isInteger(n) || n < 1) return toast.error("Minimum volunteers must be a whole number ≥ 1 (or leave blank)");
      if (maxV !== null && n > maxV) return toast.error("Minimum volunteers can't exceed the maximum");
      minV = n;
    }
    const payload = {
      title, description,
      location: remote ? (location || "Remote") : location,
      lat: remote ? null : lat,
      lng: remote ? null : lng,
      remote,
      hours_required: Number(hours),
      event_date: new Date(eventDate).toISOString(),
      closes_at: closesAt ? new Date(`${closesAt}T23:59:59`).toISOString() : null,
      values: [...values], skills_needed: [...skills],
      cover_image: coverImage || null,
      recurrence,
      recurrence_weekly_days: recurrence === "weekly" ? [...weekDays] : [],
      max_volunteers: maxV,
      min_volunteers: minV,
      commitment_type: commitmentType || null,
      collective_id: collectiveId || null,
      required_age: requiredAge ? parseInt(requiredAge, 10) : null,
      dbs_required: dbsRequired,
      campaign_id: campaignEligible && campaignOptIn ? activeCampaign.id : null,
      required_skills: [...reqSkills],
    };
    try {
      if (editId) {
        await api.updateOpportunity(editId, payload);
        toast.success("Changes saved");
        nav(`/opportunities/${editId}`);
      } else {
        const created = await api.createOpportunity(payload);
        toast.success(isOrg ? "Opportunity posted" : "Community event published!");
        if (created.geo_unresolved) toast.warning("We couldn't pin that location on the map, so it won't show in nearby searches. Edit and pick a suggested location to fix this.");
        nav(`/opportunities/${created.id}`);
      }
    } catch (e) {
      toast.error(editId ? "Could not save changes" : "Could not post");
    }
  };

  if (!loaded) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Seo title={editId ? "Edit opportunity" : "Post an opportunity"} />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">{editId ? "Edit" : isOrg ? "Post a role" : "Grassroots"}</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">
        {editId ? `Edit ${isOrg ? "opportunity" : "event"}` : isOrg ? "New opportunity" : "Host a community event"}
      </h1>
      {!isOrg && !editId && (
        <p className="text-neutral-600 mt-3 max-w-xl">A litter pick, a park tidy-up, a street library — you organise it, neighbours join in one click, and you approve their hours afterwards. It'll be badged <span className="text-terracotta font-medium">Community-led</span>.</p>
      )}

      {days.length > 0 && !editId && (
        <div className="mt-6 civic-glass rounded-2xl p-5" data-testid="post-awareness-strip">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">
            <CalendarHeart className="w-4 h-4 text-terracotta" /> Need inspiration? Coming up:
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {days.map(d => (
              <span key={d.name} className="text-xs bg-white border border-forest/10 rounded-full px-3 py-1.5 text-neutral-600">
                <span className="text-forest font-medium">{d.name}</span> · in {d.days_until}d
              </span>
            ))}
          </div>
        </div>
      )}

      <Card className="mt-8 border-neutral-200">
        <CardContent className="p-8 space-y-5">
          {ledCollectives.length > 0 && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Host under a collective</label>
              <select
                data-testid="opp-collective-select"
                value={collectiveId}
                onChange={(e) => setCollectiveId(e.target.value)}
                className="mt-2 h-11 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm text-forest focus:border-forest/60 focus:outline-none"
              >
                <option value="">No collective (post as usual)</option>
                {ledCollectives.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1.5">Collectives you belong to appear here — hosting is open to all members.</p>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Title<Req /></label>
            <Input data-testid={VOLUNTEER.newOppTitle} value={title} onChange={(e) => { setTitle(e.target.value); clearErr("title"); }} placeholder="Tree planting in Victoria Park" className={`mt-2 h-11 ${errors.title ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Description<Req /></label>
            <Textarea data-testid={VOLUNTEER.newOppDesc} value={description} onChange={(e) => { setDescription(e.target.value); clearErr("description"); }} rows={5} placeholder="What will volunteers do? What should they bring?" className={`mt-2 ${errors.description ? ERR_CLS : ""}`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Where does it happen?</label>
            <div className="flex gap-2 mt-2">
              {MODE_OPTIONS.map(opt => (
                <button key={opt.l} data-testid={`opp-mode-${opt.v ? "remote" : "inperson"}`} onClick={() => setRemote(opt.v)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${remote === opt.v ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700"}`}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          {!remote && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Location — street, area or postcode<Req /></label>
              <div className="mt-2">
                <LocationSearch
                  testId={VOLUNTEER.newOppLocation}
                  value={location}
                  placeholder="e.g. Victoria Park, London or E9 7DE"
                  inputClassName={errors.location ? ERR_CLS : ""}
                  onSelect={(r) => { setLocation(r.label); setLat(r.lat); setLng(r.lng); clearErr("location"); }}
                  onTextChange={(v) => { setLocation(v); setLat(null); setLng(null); clearErr("location"); }}
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">Pick a suggestion to pin it on the map — volunteers get directions and local alerts.</p>
              {lat != null && lng != null && (
                <div className="mt-3 space-y-2" data-testid="post-map-preview">
                  <MapLeaflet markers={previewMarkers} center={previewCenter} zoom={14} height={200} />
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-forest font-medium hover:underline">
                    <Navigation className="w-3.5 h-3.5 text-terracotta" /> Preview directions in Google Maps
                  </a>
                </div>
              )}
            </div>
          )}
          {remote && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Location label</label>
              <Input data-testid={VOLUNTEER.newOppLocation} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Remote" className="mt-2 h-11" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold pb-2">Hours required</label>
              <Input data-testid={VOLUNTEER.newOppHours} type="number" min={1} step={0.5} value={hours} onChange={(e) => setHours(e.target.value)} className="mt-auto h-11" />
            </div>
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold pb-2">Date & time<Req /></label>
              <Input data-testid={VOLUNTEER.newOppDate} type="datetime-local" value={eventDate} onChange={(e) => { setEventDate(e.target.value); clearErr("eventDate"); }} className={`mt-auto h-11 ${errors.eventDate ? ERR_CLS : ""}`} />
              {campaignEligible && (
                <label className="flex items-center gap-2 mt-2 text-sm text-forest cursor-pointer" data-testid="campaign-optin-row">
                  <input
                    type="checkbox"
                    data-testid="campaign-optin-checkbox"
                    checked={campaignOptIn}
                    onChange={(e) => setCampaignOptIn(e.target.checked)}
                  />
                  Part of {activeCampaign.name}
                </label>
              )}
            </div>
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold pb-2">Signup deadline</label>
              <Input data-testid="new-opp-closes" type="date" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="mt-auto h-11" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Max volunteers</label>
              <Input
                data-testid="opp-capacity-input"
                type="number"
                min={1}
                inputMode="numeric"
                placeholder="No limit"
                value={maxVolunteers}
                onChange={(e) => setMaxVolunteers(e.target.value.replace(/[^0-9]/g, ""))}
                className="mt-2 h-11"
              />
              <div className="text-[11px] text-neutral-500 mt-1">Leave blank for no limit. Once filled, no one can apply.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Min volunteers</label>
              <Input
                data-testid="opp-minimum-input"
                type="number"
                min={1}
                inputMode="numeric"
                placeholder="Optional"
                value={minVolunteers}
                onChange={(e) => setMinVolunteers(e.target.value.replace(/[^0-9]/g, ""))}
                className="mt-2 h-11"
              />
              <div className="text-[11px] text-neutral-500 mt-1">If fewer sign up by the deadline, you'll be asked to keep it running or cancel.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">How long does it take?</label>
              <select
                data-testid="commitment-select"
                value={commitmentType}
                onChange={(e) => setCommitmentType(e.target.value)}
                className={`mt-2 h-11 w-full rounded-md border bg-white px-3 text-sm ${editId && !commitmentType ? "border-amber-400 ring-1 ring-amber-300" : "border-neutral-200"}`}
              >
                <option value="">— pick a level —</option>
                <option value="micro">Under an hour — quick task</option>
                <option value="one_off">One-off — single event</option>
                <option value="recurring">Recurring — weekly / monthly</option>
                <option value="ongoing">Ongoing — flexible help</option>
              </select>
              {editId && !commitmentType ? (
                <div data-testid="commitment-edit-prompt" className="text-[11px] text-amber-700 mt-1">
                  Help volunteers plan — set how long this takes. Short tasks get surfaced as quick wins.
                </div>
              ) : (
                <div className="text-[11px] text-neutral-500 mt-1">Helps volunteers filter what fits their schedule.</div>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Values</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {VALUES.map(v => (
                <button key={v.id} onClick={() => toggle(values, setValues, v.id)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${values.has(v.id) ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700"}`}>{v.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Skills needed</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {SKILLS.map(s => (
                <button key={s} onClick={() => toggle(skills, setSkills, s)}
                  className={`text-sm px-3 py-1.5 rounded-full border capitalize transition-colors ${skills.has(s) ? "bg-terracotta text-white border-terracotta" : "bg-white border-neutral-200 text-neutral-700"}`}>{skillLabel(s)}</button>
              ))}
            </div>
          </div>

          {/* B4 (2026-02): tier 1-2 volunteer requirements — applicants
              self-attest; the host verifies documents themselves. */}
          <div data-testid="requirements-section" className="border border-neutral-200 rounded-xl p-5 bg-cream/40">
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-terracotta" /> Volunteer requirements (optional)
            </label>
            <p className="text-xs text-neutral-500 mt-1.5">
              Applicants confirm these themselves when applying. CivicHearth doesn't verify anything — you'll be reminded to check documents (ID, DBS certificates) yourself before the event.
            </p>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 font-bold">Minimum age</div>
              <div className="flex gap-2 mt-2">
                {[["", "No minimum"], ["16", "16+"], ["18", "18+"], ["21", "21+"]].map(([v, l]) => (
                  <button key={l} data-testid={`req-age-${v || "none"}`} onClick={() => setRequiredAge(v)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${requiredAge === v ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <button data-testid="req-dbs-toggle" onClick={() => setDbsRequired(v => !v)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${dbsRequired ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-neutral-200 text-neutral-700"}`}>
                Enhanced DBS required {dbsRequired ? "✓" : ""}
              </button>
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 font-bold">Required skills (must-have, not just nice-to-have)</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {SKILLS.map(s => (
                  <button key={s} data-testid={`req-skill-${s}`} onClick={() => toggle(reqSkills, setReqSkills, s)}
                    className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${reqSkills.has(s) ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700"}`}>{skillLabel(s)}</button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Cover image</label>
            <input data-testid="new-opp-cover" type="file" accept="image/*" onChange={onCoverChange} className="mt-2 text-sm" />
            {coverImage && <img src={`${process.env.REACT_APP_BACKEND_URL}${coverImage}`} alt="cover" className="mt-3 w-full max-h-48 object-cover rounded-xl border border-neutral-200" />}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Recurrence</label>
            <div className="flex gap-2 mt-2">
              {["none", "weekly"].map((r) => (
                <button key={r} data-testid={`recur-${r}`} onClick={() => setRecurrence(r)}
                  className={`text-sm px-3 py-1.5 rounded-full border capitalize ${recurrence === r ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700"}`}>
                  {r === "none" ? "One-off" : "Weekly"}
                </button>
              ))}
            </div>
            {recurrence === "weekly" && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                  <button key={d} data-testid={`weekday-${i}`} onClick={() => toggleDay(i)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${weekDays.has(i) ? "bg-terracotta text-white border-terracotta" : "bg-white border-neutral-200 text-neutral-700"}`}>
                    {d}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button data-testid={VOLUNTEER.newOppSubmit} onClick={submit} className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">
              {editId ? "Save changes" : "Publish"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
